import { useFormatBreederContactInfo } from '@application/api';
import { SeoController } from '@application/components';
import {
  formatApiPrice,
  formatPedigreeType,
  slugify,
  useRouter,
  useSlideshow,
  useTranslatedUrl,
} from '@application/utils';
import { CountryCode } from '@gql/graphql';
import { PLACEHOLDER_IMAGE_DOG } from '@presentation/constants';
import { StudPage } from '@presentation/pages';
import type { StudData } from '@presentation/pages/Stud/types';
import type { FC } from 'react';
import { useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useFormatBreeds } from '../BreedListPageController/api/useFormatBreeds';
import { useGetStud } from './api/useGetStud';

const DEFAULT_IMAGES = [
  { small: PLACEHOLDER_IMAGE_DOG, medium: PLACEHOLDER_IMAGE_DOG, large: PLACEHOLDER_IMAGE_DOG },
];

export const StudPageController: FC = () => {
  const { translate, infoFromCurrentPath, routeLocationDescriptor, language } = useRouter();

  const { stud, studLoading, studError, breed } = useGetStud(infoFromCurrentPath.params[1]);

  const { contactInfo } = useFormatBreederContactInfo(stud?.owner.dogBreeder);

  const slideshowItems = (stud?.imgs && stud.imgs.length > 0 ? stud?.imgs : DEFAULT_IMAGES).map(
    (img) => ({
      thumnail: img.small,
      src: (img.medium ?? img.large) as string,
    }),
  );

  const { formattedBreeds } = useFormatBreeds([breed]);

  const translations = useMemo(() => {
    if (!stud) {
      return [];
    }

    return stud?.dogInfos?.dogBreed?.translations.map((t) => ({
      language: t.language,
      url: `${slugify(t.name ?? '')}/${stud.id.toString()}`,
    }));
  }, [stud]);

  useTranslatedUrl(translations);

  const breedName = stud?.dogInfos?.dogBreed?.translations?.find(
    (t) => t.language === language,
  )?.name;

  const formattedStud = useMemo((): StudData => {
    if (!stud) return {};
    const region = stud?.owner?.dogBreeder?.account?.address?.region;

    return {
      breedName,
      hasPedigree: stud.dogInfos?.pedigree !== null,
      description: stud.studInfos?.description,
      country: stud.owner.dogBreeder?.account.address?.country,
      details: [
        {
          label: translate('glossary.breed'),
          value: breedName,
        },
        {
          label: translate('glossary.coat'),
          value: translate(`enum.dogCoat.${stud.dogInfos?.coat}`, { defaultValue: '-' }),
        },
        {
          label: translate('glossary.color'),
          value: translate(`enum.dogColor.${stud.dogInfos?.color}`, { defaultValue: '-' }),
        },
        {
          label: translate('glossary.size'),
          value: translate(`enum.animalSize.${stud.size}`, { defaultValue: '-' }),
        },
        {
          label: translate('glossary.weight'),
          value: stud?.weight
            ? translate('glossary.weightValue').replace('$weight', stud.weight.toString())
            : '-',
        },
        {
          label: translate('glossary.type'),
          value: translate(`enum.couplingType.${stud.studInfos?.type}`),
        },
        {
          label: translate('glossary.price'),
          value: stud.studInfos?.price
            ? formatApiPrice({ price: stud.studInfos?.price })
            : translate('glossary.unknownPrice'),
        },
        {
          label: translate('glossary.champion'),
          value: stud.studInfos?.champion
            ? translate('enum.yesNoUnknownStatus.YES')
            : translate('enum.yesNoUnknownStatus.NO'),
        },
        {
          label: translate('glossary.pedigree'),
          value: formatPedigreeType(translate, stud.dogInfos?.pedigree),
        },
        {
          label: translate('glossary.accomodation'),
          value: stud.studInfos?.accomodation
            ? translate('enum.yesNoUnknownStatus.YES')
            : translate('enum.yesNoUnknownStatus.NO'),
        },
        {
          label: translate('glossary.location'),
          value: region ? translate(`enum.regionCode.${region}`) : '-',
        },
      ],
    } as StudData;
  }, [breedName, stud, translate]);

  const replaceNameRegionCountry = (str: string) => {
    const region = stud?.owner?.dogBreeder?.account?.address?.region;
    const country = stud?.owner?.dogBreeder?.account?.address?.country ?? CountryCode.BE;
    return str
      .replace('$name', breedName ?? '')
      .replace('$region', region ? translate(`enum.regionCode.${region}`) : '')
      .replace(
        '$country',
        translate(`enum.country.${country}`, {
          defaultValue: '',
        }),
      );
  };

  return (
    <>
      <SeoController
        title={replaceNameRegionCountry(translate('studDetails.dynamicSeo.title'))}
        description={replaceNameRegionCountry(translate('studDetails.dynamicSeo.description'))}
        thumbnail={slideshowItems[0].src}
      />

      {!studLoading && (studError || !stud) && <Navigate replace to="/404" />}

      <StudPage
        studLoading={studLoading}
        studData={formattedStud}
        contactInfoData={contactInfo}
        breedCardData={formattedBreeds?.[0]}
        slideshowData={useSlideshow(slideshowItems)}
        moreBreedersLink={routeLocationDescriptor('breederList', {
          search: { breeds: [stud?.dogInfos?.dogBreed?.name] },
        })}
      />
    </>
  );
};
